// src/pages/Orders/Orders.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, collection, query, orderBy, where } from 'firebase/firestore';

import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { UsageLimitGuard } from '../../components/subscription/SubscriptionGuard';
import * as XLSX from 'xlsx';
import UnshippedProductsModal from '../../components/orders/UnshippedProductsModal';

// --- STYLES ---
// --- STYLES ---
const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent',
        height: '100%',
        overflow: 'auto',
        padding: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '4px'
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
    },
    summaryCard: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e2e8f0',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'default'
    },
    statIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
        fontSize: '24px'
    },
    statTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        marginBottom: '4px'
    },
    statValue: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#0f172a',
        letterSpacing: '-0.5px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '32px',
        border: '1px solid #e2e8f0'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        marginTop: '16px'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '13px',
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    },
    td: {
        padding: '20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '15px',
        color: '#334155',
        verticalAlign: 'middle'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    actionButtons: { display: 'flex', gap: '8px' },
    actionBtn: {
        padding: '8px 16px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '13px',
        cursor: 'pointer',
        background: 'white',
        color: '#06C167',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
    },
    tabContainer: {
        display: 'flex',
        gap: '4px',
        padding: '4px',
        background: '#f1f5f9',
        borderRadius: '12px',
        width: 'fit-content',
        marginBottom: '24px'
    },
    tab: {
        padding: '8px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        color: '#64748b',
        transition: 'all 0.2s'
    },
    activeTab: {
        background: 'white',
        color: '#0f172a',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    backLink: { textDecoration: 'none', color: '#06C167', marginBottom: '15px', display: 'inline-block', fontWeight: '600', fontSize: '14px' },
    filterContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    searchInput: {
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        width: '320px',
        background: 'white',
        outline: 'none',
        transition: 'all 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    searchInputContainer: {
        position: 'relative'
    },
    searchIcon: {
        position: 'absolute',
        left: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '18px',
        height: '18px'
    },
    exportButton: {
        padding: '12px 24px',
        border: '1px solid #e2e8f0',
        background: 'white',
        color: '#374151',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: '20px',
        fontWeight: '600',
        fontSize: '12px',
        gap: '6px'
    },
    paymentBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: '20px',
        fontWeight: '600',
        fontSize: '12px',
        gap: '6px'
    },
    paymentStatusPaid: { background: '#dcfce7', color: '#166534', border: '1px solid #bbb' }, // This border overrides might be too much, keeping simple
    formGroup: { marginBottom: '16px' },
    input: { padding: '12px 16px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', width: '100%', background: '#fff', outline: 'none' },
    suggestions: { position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
    suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' },
    customerLink: {
        color: '#06C167',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: '600',
        transition: 'color 0.2s',
        '&:hover': {
            color: '#4f46e5',
            textDecoration: 'underline'
        }
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        background: '#f8fafc',
        borderRadius: '16px',
        border: '2px dashed #e2e8f0',
        margin: '32px 0'
    }
};

// PDF functions migrated to src/utils/pdfGenerator.js

// OrderDetailView and PDFOptionModal moved to OrderDetail.jsx or handled by routing.

// --- REMOVED OBSOLETE CODE CHUNK 1 ---


function Orders() {
    const { userData } = useAuth();
    const { hasRestriction } = useSubscription();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [companyData, setCompanyData] = useState(null);
    const [useDynamicRates, setUseDynamicRates] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState([]); // Empty = All, otherwise specific statuses
    const [isUnshippedModalOpen, setIsUnshippedModalOpen] = useState(false);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }
        const companyId = userData.companyId;

        const qOrders = query(collection(db, 'companies', companyId, 'orders'), orderBy('orderDate', 'desc'));
        const unsubOrders = onSnapshot(qOrders, (snapshot) => {
            setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        const qPayments = query(collection(db, 'companies', companyId, 'transactions'), where('type', '==', 'Tahsilat'));
        const unsubPayments = onSnapshot(qPayments, (snapshot) => {
            setPayments(snapshot.docs.map(doc => doc.data()));
        });

        return () => { unsubOrders(); unsubPayments(); };
    }, [userData]);

    // Fetch company data for currency settings
    useEffect(() => {
        if (!userData?.companyId) return;
        const unsubCompany = onSnapshot(doc(db, 'companies', userData.companyId), (doc) => {
            if (doc.exists()) {
                setCompanyData(doc.data());
            }
        });
        return () => unsubCompany();
    }, [userData]);

    const toggleStatus = (status) => {
        setSelectedStatuses(prev => {
            if (prev.includes(status)) {
                return prev.filter(s => s !== status);
            } else {
                return [...prev, status];
            }
        });
    };

    const ordersWithPaymentStatus = useMemo(() => {
        // Create a map of total paid amount per orderId
        const paidMap = {};
        payments.forEach(p => {
            if (p.orderId) {
                paidMap[p.orderId] = (paidMap[p.orderId] || 0) + (p.amount || 0);
            }
        });

        return orders.map(order => {
            const paidAmount = paidMap[order.id] || 0;
            const totalAmount = order.total || 0;
            const paymentPercentage = totalAmount > 0 ? Math.min(100, (paidAmount / totalAmount) * 100) : 0;
            
            let status = order.paymentStatus || (paidAmount >= totalAmount - 0.01 ? 'Ödeme Alındı' : (paidAmount > 0 ? 'Kısmi Ödendi' : 'Ödeme Bekleniyor'));
            if (typeof status === 'string' && status.trim().toLowerCase() === 'ödeme bekleniyor') {
                status = 'Ödeme Bekleniyor';
            }

            return {
                ...order,
                effectivePaymentStatus: status,
                paidAmount,
                paymentPercentage
            };
        });
    }, [orders, payments]);

    const filteredOrders = useMemo(() => {
        return ordersWithPaymentStatus.filter(order => {
            const matchesSearch =
                order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (order.orderNumber || order.id.substring(0, 6)).toLowerCase().includes(searchTerm.toLowerCase());

            let matchesStatus = true;
            if (selectedStatuses.length > 0) {
                // Map UI statuses to DB statuses
                const dbStatuses = [];
                if (selectedStatuses.includes('pending')) dbStatuses.push('Sipariş Alındı');
                if (selectedStatuses.includes('partial')) dbStatuses.push('Kısmen Gönderildi');
                if (selectedStatuses.includes('completed')) dbStatuses.push('Tamamlandı');

                if (dbStatuses.length > 0) {
                    matchesStatus = dbStatuses.includes(order.status);
                }
            }

            return matchesSearch && matchesStatus;
        });
    }, [ordersWithPaymentStatus, searchTerm, selectedStatuses]);

    const handleExport = () => {
        // Check if Excel export is allowed for current subscription
        if (!hasRestriction('excelExport')) {
            alert('Excel dışa aktarma özelliği sadece ücretli planlarda mevcuttur. Lütfen aboneliğinizi yükseltin.');
            return;
        }

        const worksheetData = filteredOrders.map(order => ({
            'Sipariş No': order.orderNumber || order.id.substring(0, 6),
            'Müşteri': order.customerName,
            'Tarih': new Date(order.orderDate.seconds * 1000).toLocaleDateString(),
            [`Tutar (${order.currency === 'USD' ? '$' : '₺'})`]: order.total,
            'Sevkiyat Durumu': order.status,
            'Ödeme Durumu': order.effectivePaymentStatus
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sipariş Listesi");
        const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
        XLSX.writeFile(workbook, `Siparis-Listesi-${currentDate}.xlsx`);
    };

    // Özet kartlar için hesaplamalar
    const totalOrders = orders.length;
    const waitingOrders = orders.filter(o => o.status === 'Sipariş Alındı').length;
    const partialOrders = orders.filter(o => o.status === 'Kısmen Gönderildi').length;
    const completedOrders = orders.filter(o => o.status === 'Tamamlandı').length;
    const totalAmount = orders.reduce((sum, o) => sum + (o.total || 0), 0);




    return (
        <div className="orders-container">
            <style>
                {`
                    .orders-container {
                        font-family: 'Inter', -apple-system, sans-serif;
                        padding: 20px;
                        background: transparent;
                        height: 100%;
                        overflow: auto;
                    }
                    .orders-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 32px;
                        gap: 16px;
                    }
                    .orders-title {
                        font-size: 28px;
                        font-weight: 800;
                        color: #1e293b;
                        margin: 0;
                    }
                    .orders-subtitle {
                        color: #64748b;
                        margin: 4px 0 0 0;
                        font-size: 14px;
                    }
                    .summary-cards {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                        gap: 12px;
                        margin-bottom: 24px;
                    }
                    .filter-box {
                        background: white;
                        padding: 24px;
                        border-radius: 16px;
                        border: 1px solid #e2e8f0;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                        margin-bottom: 24px;
                    }
                    .filter-row {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 16px;
                    }
                    .search-container {
                        position: relative;
                        flex: 1;
                        min-width: 250px;
                    }
                    .responsive-table-wrapper {
                        width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                    }
                    
                    @media (max-width: 768px) {
                        .orders-header {
                            flex-direction: column;
                            align-items: flex-start;
                        }
                        .header-buttons {
                            width: 100%;
                            display: flex;
                            flex-direction: column;
                            gap: 8px;
                        }
                        .header-buttons a, .header-buttons button {
                            width: 100%;
                            justify-content: center;
                        }
                        .filter-row {
                            flex-direction: column;
                            align-items: stretch;
                        }
                        .search-container {
                            width: 100%;
                        }
                        .filter-actions {
                            display: flex;
                            flex-wrap: wrap;
                            gap: 8px;
                        }
                        .filter-actions button {
                            flex: 1;
                            justify-content: center;
                        }
                        .summary-cards {
                            grid-template-columns: repeat(2, 1fr);
                        }
                    }

                    @media (max-width: 480px) {
                        .summary-cards {
                            grid-template-columns: 1fr;
                        }
                        .orders-title {
                            font-size: 22px;
                        }
                        .tab-container {
                            overflow-x: auto;
                            white-space: nowrap;
                            padding-bottom: 4px;
                            width: 100%;
                        }
                    }
                `}
            </style>
            <div className="orders-header">
                <div style={styles.headerLeft}>
                    <h1 className="orders-title">Siparişler</h1>
                    <p className="orders-subtitle">Siparişleri yönetin ve takip edin</p>
                </div>
                <div className="header-buttons" style={{ display: 'flex', gap: '12px' }}>
                    <Link to="/siparisler/faturalastirilmamis-sevkiyatlar" style={{ ...styles.button, background: '#10b981' }}>
                        🧾 Faturalaştırılmamış Sevkiyatlar
                    </Link>
                    <Link to="/siparisler/toplu-yukle" style={{ ...styles.button, background: '#64748b' }}>
                        📥 Toplu Sipariş Yükle
                    </Link>
                    <UsageLimitGuard
                        type="orders"
                        fallback={
                            <button
                                style={{ ...styles.button, opacity: 0.6, cursor: 'not-allowed' }}
                                onClick={() => alert('Sipariş limitiniz doldu. Aboneliğinizi yükseltmek için Abonelik sayfasını ziyaret edin.')}
                            >
                                + Yeni Sipariş Oluştur (Limit Doldu)
                            </button>
                        }
                    >
                        <Link to="/siparisler/yeni" style={styles.button}>
                            + Yeni Sipariş Oluştur
                        </Link>
                    </UsageLimitGuard>
                </div>
            </div>

            {/* Stat Cards - Compact Version */}
            <div className="summary-cards">
                <div style={{ ...styles.summaryCard, padding: '16px' }}>
                    <div style={{ ...styles.statIcon, width: '32px', height: '32px', fontSize: '16px', background: '#eff6ff', color: '#3b82f6' }}>📦</div>
                    <div style={{ marginTop: '8px' }}>
                        <div style={{ ...styles.statTitle, fontSize: '12px' }}>Toplam</div>
                        <div style={{ ...styles.statValue, fontSize: '18px' }}>{totalOrders}</div>
                    </div>
                </div>
                <div style={{ ...styles.summaryCard, padding: '16px' }}>
                    <div style={{ ...styles.statIcon, width: '32px', height: '32px', fontSize: '16px', background: '#fffbeb', color: '#f59e0b' }}>⏳</div>
                    <div style={{ marginTop: '8px' }}>
                        <div style={{ ...styles.statTitle, fontSize: '12px' }}>Bekleyen</div>
                        <div style={{ ...styles.statValue, fontSize: '18px' }}>{waitingOrders}</div>
                    </div>
                </div>
                <div style={{ ...styles.summaryCard, padding: '16px' }}>
                    <div style={{ ...styles.statIcon, width: '32px', height: '32px', fontSize: '16px', background: '#ecfeff', color: '#06b6d4' }}>🚚</div>
                    <div style={{ marginTop: '8px' }}>
                        <div style={{ ...styles.statTitle, fontSize: '12px' }}>İşlemde</div>
                        <div style={{ ...styles.statValue, fontSize: '18px' }}>{partialOrders}</div>
                    </div>
                </div>
                <div style={{ ...styles.summaryCard, padding: '16px' }}>
                    <div style={{ ...styles.statIcon, width: '32px', height: '32px', fontSize: '16px', background: '#f0fdf4', color: '#22c55e' }}>✅</div>
                    <div style={{ marginTop: '8px' }}>
                        <div style={{ ...styles.statTitle, fontSize: '12px' }}>Tamamlanan</div>
                        <div style={{ ...styles.statValue, fontSize: '18px' }}>{completedOrders}</div>
                    </div>
                </div>
                <div style={{ ...styles.summaryCard, padding: '16px' }}>
                    <div style={{ ...styles.statIcon, width: '32px', height: '32px', fontSize: '16px', background: '#f8fafc', color: '#64748b' }}>💰</div>
                    <div style={{ marginTop: '8px' }}>
                        <div style={{ ...styles.statTitle, fontSize: '12px' }}>Ciro</div>
                        <div style={{ ...styles.statValue, fontSize: '18px' }}>{totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₺</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filter-box">
                <div className="filter-row">
                    <div className="tab-container" style={styles.tabContainer}>
                        <button
                            style={{ ...styles.tab, ...(selectedStatuses.length === 0 ? styles.activeTab : {}) }}
                            onClick={() => setSelectedStatuses([])}
                        >
                            Tümü
                        </button>
                        <button
                            style={{ ...styles.tab, ...(selectedStatuses.includes('pending') ? styles.activeTab : {}) }}
                            onClick={() => toggleStatus('pending')}
                        >
                            Bekleyen
                        </button>
                        <button
                            style={{ ...styles.tab, ...(selectedStatuses.includes('partial') ? styles.activeTab : {}) }}
                            onClick={() => toggleStatus('partial')}
                        >
                            İşlemde
                        </button>
                        <button
                            style={{ ...styles.tab, ...(selectedStatuses.includes('completed') ? styles.activeTab : {}) }}
                            onClick={() => toggleStatus('completed')}
                        >
                            Tamamlanan
                        </button>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            onClick={() => setUseDynamicRates(!useDynamicRates)}
                            style={{
                                ...styles.tab,
                                ...(useDynamicRates ? styles.activeTab : {}),
                                border: useDynamicRates ? '1px solid #e2e8f0' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                            title={useDynamicRates ? "Güncel kurlarla TL karşılığı (Ayarlar'daki kurlar baz alınır)" : "Sipariş tarihindeki orijinal değerler"}
                        >
                            {useDynamicRates ? '💱 Güncel Kur (TR)' : '📅 İşlem Kuru (ORJ)'}
                        </button>
                    </div>

                    <div className="filter-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div className="search-container" style={styles.searchInputContainer}>
                            <svg style={styles.searchIcon} fill="currentColor" viewBox="0 0 24 24">
                                <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Müşteri veya Sipariş No..."
                                style={{ ...styles.searchInput, width: '100%' }}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button onClick={handleExport} style={styles.exportButton}>Excel</button>
                        <button
                            onClick={() => setIsUnshippedModalOpen(true)}
                            style={{
                                ...styles.exportButton,
                                background: '#fff7ed',
                                color: '#c2410c',
                                border: '1px solid #ffedd5'
                            }}
                            title="Tüm siparişlerdeki gönderilmeyen ürünlerin listesini Excel olarak indirir"
                        >
                            📦 Gönderilmeyenler
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>Yükleniyor...</div>
                ) : filteredOrders.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Sipariş Bulunamadı</div>
                        <div style={{ fontSize: '14px', color: '#64748b' }}>Arama kriterlerinize uygun sipariş yok.</div>
                    </div>
                ) : (
                    <div className="responsive-table-wrapper">
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Sipariş No</th>
                                    <th style={styles.th}>Müşteri</th>
                                    <th style={styles.th}>Tarih</th>
                                    <th style={styles.th}>Tutar</th>
                                    <th style={styles.th}>Sevkiyat</th>
                                    <th style={styles.th}>Ödeme</th>
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map(order => (
                                    <tr key={order.id}
                                        style={styles.trHover}
                                        onClick={() => navigate(`/siparisler/${order.id}`)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <td style={{ ...styles.td, fontWeight: '600', color: '#06C167' }}>
                                            #{order.orderNumber || order.id.substring(0, 6)}
                                        </td>
                                        <td style={{ ...styles.td, fontWeight: '500' }}>{order.customerName}</td>
                                        <td style={styles.td}>{new Date(order.orderDate.seconds * 1000).toLocaleDateString('tr-TR')}</td>
                                        <td style={{ ...styles.td, fontWeight: '600' }}>
                                            {useDynamicRates ? (
                                                <>
                                                    <span>{(function (o) {
                                                        const amount = o.total || 0;
                                                        const currency = o.currency || 'TRY';
                                                        if (currency === 'TRY') return amount;
                                                        const rate = companyData?.currencySettings?.[currency];
                                                        return rate ? amount * rate : 0;
                                                    })(order).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺</span>
                                                </>
                                            ) : (
                                                `${order.total.toFixed(2)} ${order.currency === 'USD' ? '$' : '₺'}`
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: order.status === 'Tamamlandı' ? '#f0fdf4' : order.status === 'Kısmen Gönderildi' ? '#eff6ff' : '#fff7ed',
                                                color: order.status === 'Tamamlandı' ? '#166534' : order.status === 'Kısmen Gönderildi' ? '#1d4ed8' : '#c2410c',
                                                border: '1px solid ' + (order.status === 'Tamamlandı' ? '#bbf7d0' : order.status === 'Kısmen Gönderildi' ? '#bfdbfe' : '#fed7aa')
                                            }}>
                                                {order.status === 'Tamamlandı' ? 'Tamamlandı' : order.status === 'Kısmen Gönderildi' ? 'Kısmen' : 'Bekliyor'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px' }}>
                                                <span style={{
                                                    ...styles.paymentBadge,
                                                    ...(order.effectivePaymentStatus === 'Ödeme Alındı' ? styles.paymentStatusPaid :
                                                        order.effectivePaymentStatus === 'Kısmi Ödendi' ? styles.paymentStatusPartial :
                                                            styles.paymentStatusPending),
                                                    padding: '4px 8px',
                                                    fontSize: '11px'
                                                }}>{order.effectivePaymentStatus}</span>
                                                <div style={{
                                                    width: '100%',
                                                    height: '4px',
                                                    background: '#e2e8f0',
                                                    borderRadius: '2px',
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        width: `${order.paymentPercentage}%`,
                                                        height: '100%',
                                                        background: order.paymentPercentage >= 100 ? '#10b981' : '#06C167',
                                                        transition: 'width 0.3s ease'
                                                    }} />
                                                </div>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <button style={styles.actionBtn}>Detay →</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Drawer logic removed. Direct navigation is used instead. */}

            <UnshippedProductsModal 
                isOpen={isUnshippedModalOpen} 
                onClose={() => setIsUnshippedModalOpen(false)} 
                orders={orders} 
                userData={userData} 
            />
        </div>
    );
}

// --- MODAL BİLEŞENİ ---


export default Orders;
